import { useMemo } from 'react';
import { useBusinessStore } from '../store/useBusinessStore';
import { useAuthStore } from '../store/useAuthStore';
import { supabase } from '../supabaseClient';
import toast from 'react-hot-toast';

const DEMO_BUSINESS_IDS = ['9001', '9002', '9003'];

const DEMO_LEADS = [
  { id: 'demo-lead-1', client: 'Carlos Mendoza', client_name: 'Carlos Mendoza', phone: '+573001112233', service: 'Corte Fade & Barba', time: '10:30 AM', status: 'pendiente', price: 45000 },
  { id: 'demo-lead-2', client: 'Andrés López', client_name: 'Andrés López', phone: '+573004445566', service: 'Perfilado de Barba + Spa', time: '11:15 AM', status: 'confirmado', price: 35000 },
  { id: 'demo-lead-3', client: 'María García', client_name: 'María García', phone: '+573108889900', service: 'Tinte & Tratamiento Capilar', time: '02:00 PM', status: 'en proceso', price: 120000 },
  { id: 'demo-lead-4', client: 'Roberto Gómez', client_name: 'Roberto Gómez', phone: '+573152223344', service: 'Corte Clásico + Masaje', time: '04:30 PM', status: 'completado', price: 50000 },
  { id: 'demo-lead-5', client: 'Lucía Fernández', client_name: 'Lucía Fernández', phone: '+573205557788', service: 'Asesoría de Imagen VIP', time: '06:00 PM', status: 'confirmado', price: 80000 },
  { id: 'demo-lead-6', client: 'Camilo Torres', client_name: 'Camilo Torres', phone: '+573016664422', service: 'Corte Infantil', time: 'Ayer', status: 'cancelado', price: 30000 }
];

const normalizeStatus = (rawStatus) => {
  if (!rawStatus) return 'pendiente';
  const s = String(rawStatus).toLowerCase().trim();
  if (s === 'scheduled' || s === 'confirmada' || s === 'confirmado') return 'confirmado';
  if (s === 'pending' || s === 'pendiente') return 'pendiente';
  if (s === 'in_progress' || s === 'en_proceso' || s === 'en proceso') return 'en proceso';
  if (s === 'completed' || s === 'completada' || s === 'completado') return 'completado';
  if (s === 'cancelled' || s === 'cancelada' || s === 'cancelado') return 'cancelado';
  return s;
};

export const useKanban = () => {
  const { masterDb, activeBusinessId, setMasterDb } = useBusinessStore();
  const { userRole } = useAuthStore();
  
  const activeBusiness = useMemo(() => {
    if (!Array.isArray(masterDb)) return null;
    return masterDb.find(b => String(b.id) === String(activeBusinessId)) || null;
  }, [masterDb, activeBusinessId]);

  const isDemo = DEMO_BUSINESS_IDS.includes(String(activeBusinessId));
  const isReadOnlyDemo = isDemo && userRole !== 'master';

  // Obtener y normalizar citas/leads
  const appointments = useMemo(() => {
    const rawAppointments = Array.isArray(activeBusiness?.appointments) ? activeBusiness.appointments : [];
    
    // Si está vacío y es cuenta demo, usamos semillas interactivas
    const sourceList = rawAppointments.length > 0 ? rawAppointments : (isDemo ? DEMO_LEADS : []);

    return sourceList.map(appt => ({
      id: appt.id || `lead-${Math.random().toString(36).substr(2, 9)}`,
      client: appt.client || appt.client_name || appt.nombre_cliente || appt.cliente || 'Cliente',
      phone: appt.phone || appt.client_phone || appt.telefono || '',
      service: appt.service || appt.servicio || appt.service_name || 'Servicio General',
      time: appt.time || (appt.start_time ? new Date(appt.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Hoy'),
      status: normalizeStatus(appt.status),
      price: appt.price || appt.precio || 0,
      notes: appt.notes || appt.notas || ''
    }));
  }, [activeBusiness, isDemo]);

  const handleUpdateKanbanStatus = async (appointmentId, newStatus) => {
    if (!activeBusinessId) return;

    const normalizedTargetStatus = normalizeStatus(newStatus);
    const previousStatus = appointments.find(appt => String(appt.id) === String(appointmentId))?.status;

    const applyStatus = (targetStatus) => {
      setMasterDb(prev => {
        if (!Array.isArray(prev)) return prev;
        return prev.map(b => {
          if (String(b.id) === String(activeBusinessId)) {
            const currentList = Array.isArray(b.appointments) && b.appointments.length > 0
              ? b.appointments
              : (isDemo ? DEMO_LEADS : []);

            return {
              ...b,
              appointments: currentList.map(appt =>
                String(appt.id) === String(appointmentId)
                  ? { ...appt, status: targetStatus }
                  : appt
              )
            };
          }
          return b;
        });
      });
    };

    // 1. Optimistic UI update en el estado local de Zustand
    applyStatus(normalizedTargetStatus);

    // 2. Si es cuenta demo con permisos restringidos, permitimos la interactividad visual sin error RLS
    if (isReadOnlyDemo) {
      toast.success('Estado actualizado (Simulación Demo)');
      return;
    }

    // 3. Si es cuenta real o Master, persistir en Supabase
    try {
      const isMockId = String(appointmentId).startsWith('demo-') || String(appointmentId).startsWith('mock-');
      if (!isMockId) {
        const { error } = await supabase
          .from('appointments')
          .update({ status: normalizedTargetStatus })
          .eq('id', appointmentId);

        if (error) {
          console.warn('Supabase update warning:', error.message);
          toast.error('No se pudo guardar el cambio de estado. Intenta de nuevo.');
          if (previousStatus !== undefined) applyStatus(previousStatus);
          return;
        }
      }
      toast.success('Estado actualizado');
    } catch (error) {
      console.error('Error updating status in DB:', error);
      // Mantenemos la UI optimista para fluidez
      toast.success('Estado actualizado localmente');
    }
  };

  return {
    appointments,
    handleUpdateKanbanStatus,
    isReadOnlyDemo
  };
};
